/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import type { Ref } from 'preact/hooks';
import { ReferenceLine } from '../utils/UNSAFE_meterUtils';

const FIT_REF_BAR_PROTRUSION = 16;

type Dimensions = {
  width: number;
  height: number;
};

/**
 * Returns the metric value of the clicked point.
 * @param event The PointerEvent
 * @param dimensionsRef Ref to stored dimensions of the meter bar.
 * @param min The minimum value of the meter bar.
 * @param max The maximum value of the meter bar.
 * @param step The step value of the meter bar.
 * @param orientation The orientation of the meter bar.
 * @param isRtl Whether the reading mode is 'rtl'
 * @returns The metric value of the clicked point.
 */
export function getValue(
  event: PointerEvent,
  dimensionsRef: Ref<Dimensions>,
  min: number,
  max: number,
  step: number,
  orientation?: 'horizontal' | 'vertical',
  isRtl?: boolean
): number | undefined {
  const dims = dimensionsRef.current;
  if (!dims || dims.width === 0) {
    return undefined;
  }

  let val;

  if (orientation === 'horizontal') {
    const width = event.offsetX;
    val = min + ((max - min) * (isRtl ? dims.width - width : width)) / dims.width;
  } else {
    const height = dims.height - event.offsetY;
    val = min + ((max - min) * height) / dims.height;
  }
  val = Math.round(val / step) * step;
  return Math.max(min, Math.min(max, val));
}

/**
 * Returns the true dimension of the meter bar after first render. All dimensions are 0 for initial render.
 * @param element The meter bar track HTML element
 * @returns The dimension of the meter bar.
 */
export function getDimensions(element: HTMLElement | null): Dimensions {
  if (!element) {
    return {
      width: 0,
      height: 0
    };
  }
  const trackDims = element.getBoundingClientRect();

  return {
    width: trackDims.width,
    height: trackDims.height
  };
}

/**
 * Returns object with 'start' and 'end' keys. Value for each key will be true if at least one ref line with correposnding position is present in meter bar.
 * @param lines The array of reference lines.
 * @returns Returns object with start and end keys.
 */
export function getRefLinesInfo(lines?: ReferenceLine[]): { start: boolean; end: boolean } {
  let refLineInfo = { start: false, end: false };
  if (lines) {
    refLineInfo = lines.reduce((acc, cur) => {
      acc.start = acc.start || cur.position === 'start';
      acc.end = acc.end || cur.position === 'end' || cur.position == null;
      return acc;
    }, refLineInfo);
  }
  return refLineInfo;
}

export const getMarginClass = function (
  info: { start: boolean; end: boolean },
  isHoriz: boolean,
  barTrackMarginTopStyle: string,
  barTrackMarginStartStyle: string,
  barTrackMarginBottomStyle: string,
  barTrackMarginEndStyle: string
) {
  const className = [];

  if (info.start) {
    className.push(isHoriz ? barTrackMarginTopStyle : barTrackMarginStartStyle);
  }

  if (info.end) {
    className.push(isHoriz ? barTrackMarginBottomStyle : barTrackMarginEndStyle);
  }
  return className;
};

export const getFitMarginClass = function (
  info: { start: boolean; end: boolean },
  isHoriz: boolean,
  barTrackMarginTopFitStyle: string,
  barTrackMarginStartFitStyle: string,
  barTrackMarginBottomFitStyle: string,
  barTrackMarginEndFitStyle: string
) {
  const className = [];

  if (info.start) {
    className.push(isHoriz ? barTrackMarginTopFitStyle : barTrackMarginStartFitStyle);
  }

  if (info.end) {
    className.push(isHoriz ? barTrackMarginBottomFitStyle : barTrackMarginEndFitStyle);
  }
  return className;
};

export const getFitTrackWidth = function (
  info: { start: boolean; end: boolean },
  width?: number,
  isHoriz?: boolean
) {
  if (width) {
    if (info.start && !isHoriz) {
      width = width - FIT_REF_BAR_PROTRUSION;
    }

    if (info.end && !isHoriz) {
      width = width - FIT_REF_BAR_PROTRUSION;
    }
  }

  return width;
};

export const getFitTrackHeight = function (
  info: { start: boolean; end: boolean },
  height?: number,
  isHoriz?: boolean
) {
  if (height) {
    if (info.start && isHoriz) {
      height = height - FIT_REF_BAR_PROTRUSION;
    }
    if (info.end && isHoriz) {
      height = height - FIT_REF_BAR_PROTRUSION;
    }
  }
  return height;
};
